/**
 * Superadmin ダッシュボード (SA-1).
 * /admin のトップページ。統計情報 + 直近登録 + トライアル期限切れ間近。
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { API_BASE } from '../../lib/api-client.js';

type ExpiringSite = {
  id: string;
  name: string;
  ownerEmail: string | null;
  ownerUserId: string;
  trialEndsAt: number;
  daysRemaining: number;
};

export function AdminDashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/admin/stats`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch stats');
      return res.json() as Promise<{
        totalUsers: number;
        totalSites: number;
        recentUsers: Array<{ id: string; email: string; createdAt: number }>;
        recentLogins: Array<{
          id: string;
          userId: string;
          method: string;
          success: number;
          createdAt: number;
        }>;
      }>;
    },
  });

  const { data: expiring } = useQuery({
    queryKey: ['admin', 'trials-expiring'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/admin/trials/expiring?days=7`, {
        credentials: 'include',
      });
      if (!res.ok) return { sites: [] };
      return (await res.json()) as { sites: ExpiringSite[] };
    },
    staleTime: 60_000,
  });

  if (isLoading) return <div>読み込み中...</div>;
  if (error) return <div>エラーが発生しました</div>;

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24 }}>管理者ダッシュボード</h1>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 16,
          marginBottom: 32,
        }}
      >
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: 8,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ fontSize: 14, color: '#64748b' }}>総ユーザー数</div>
          <div style={{ fontSize: 36, fontWeight: 700 }}>{data?.totalUsers ?? 0}</div>
        </div>
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: 8,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ fontSize: 14, color: '#64748b' }}>総サイト数</div>
          <div style={{ fontSize: 36, fontWeight: 700 }}>{data?.totalSites ?? 0}</div>
        </div>
      </div>

      {/* トライアル期限切れ間近 */}
      {expiring && expiring.sites.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>
            トライアル期限切れ間近（7日以内）
          </h2>
          <table
            style={{
              width: '100%',
              backgroundColor: '#fff',
              borderRadius: 8,
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#fef3c7' }}>
                <th style={thCell}>サイト名</th>
                <th style={thCell}>オーナー</th>
                <th style={thCell}>残り日数</th>
                <th style={thCell}>期限日</th>
              </tr>
            </thead>
            <tbody>
              {expiring.sites.map((s) => (
                <tr key={s.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={tdCell}>
                    <Link
                      to="/sites/$siteId"
                      params={{ siteId: s.id }}
                      style={{ color: '#3b82f6', textDecoration: 'none' }}
                    >
                      {s.name}
                    </Link>
                  </td>
                  <td style={tdCell}>
                    {s.ownerEmail && (
                      <Link
                        to="/users/$userId"
                        params={{ userId: s.ownerUserId }}
                        style={{ color: '#3b82f6', textDecoration: 'none' }}
                      >
                        {s.ownerEmail}
                      </Link>
                    )}
                  </td>
                  <td style={{ ...tdCell, color: s.daysRemaining <= 1 ? '#dc2626' : '#d97706' }}>
                    {s.daysRemaining} 日
                  </td>
                  <td style={{ ...tdCell, color: '#64748b' }}>
                    {new Date(s.trialEndsAt).toLocaleDateString('ja-JP')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>直近の登録ユーザー</h2>
      <table
        style={{
          width: '100%',
          backgroundColor: '#fff',
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <thead>
          <tr style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, color: '#64748b' }}>
              メール
            </th>
            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, color: '#64748b' }}>
              登録日時
            </th>
          </tr>
        </thead>
        <tbody>
          {data?.recentUsers?.map((u) => (
            <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
              <td style={{ padding: '10px 16px', fontSize: 14 }}>
                <Link
                  to="/users/$userId"
                  params={{ userId: u.id }}
                  style={{ color: '#3b82f6', textDecoration: 'none' }}
                >
                  {u.email}
                </Link>
              </td>
              <td style={{ padding: '10px 16px', fontSize: 14, color: '#64748b' }}>
                {new Date(u.createdAt).toLocaleString('ja-JP')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const thCell: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: 13,
  color: '#64748b',
  fontWeight: 600,
};

const tdCell: React.CSSProperties = {
  padding: '10px 16px',
  fontSize: 14,
};
